import {
  ALL_BENEFICIARIES,
  NOTIFY_OPTIONS,
  formatLocalShort,
  googleCalendarLink,
  notifyAtFor,
  toFortalezaLocal,
  validateInterview,
} from '@sales-rules';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { salesApi, type SalesInterview, type SalesProposal } from '../../lib/sales';

/**
 * Entrevistas médicas na tela de Vendas (V3): a janela "+ Entrevista", as linhas no card,
 * o painel dos próximos 7 dias e o aviso na hora marcada (com bipe). O aviso também sai
 * pelo sino do CRM, pelo servidor — este aqui é o da tela aberta.
 */

/** A hora atual (ms), atualizada a cada `ms`: o painel e o aviso andam sozinhos. */
function useClock(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(timer);
  }, [ms]);
  return now;
}

const calendarLink = (proposal: SalesProposal, interview: SalesInterview) =>
  googleCalendarLink({
    who: interview.who,
    company: proposal.company,
    requestNumber: proposal.requestNumber,
    at: interview.at,
    notifyAt: interview.notifyAt,
  });

/** "14:30 / hoje", "09:00 / amanhã" ou "30/10 / 14:30". */
function when(local: string, clock: number): { top: string; sub: string } {
  const today = toFortalezaLocal(new Date(clock)).slice(0, 10);
  const tomorrow = toFortalezaLocal(new Date(clock + 86_400_000)).slice(0, 10);
  const day = local.slice(0, 10);
  const time = local.slice(11, 16);
  if (day === today) return { top: time, sub: 'hoje' };
  if (day === tomorrow) return { top: time, sub: 'amanhã' };
  return { top: `${local.slice(8, 10)}/${local.slice(5, 7)}`, sub: time };
}

export function InterviewDialog({
  proposal,
  onClose,
  onSaved,
}: {
  proposal: SalesProposal;
  onClose: () => void;
  onSaved: (saved: SalesProposal, notifyAt: string) => void;
}) {
  const names = proposal.beneficiaries.map((item) => item.name).filter(Boolean);
  const options = [...(names.length > 1 ? [ALL_BENEFICIARIES] : []), ...names];
  const [who, setWho] = useState(options[0] ?? 'TITULAR');
  const [date, setDate] = useState(() =>
    toFortalezaLocal(new Date(Date.now() + 86_400_000)).slice(0, 10),
  );
  const [time, setTime] = useState('');
  const [option, setOption] = useState('60');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const timeInput = useRef<HTMLInputElement>(null);
  useEffect(() => timeInput.current?.focus(), []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!date || !time) {
      setError('Informe a data e a hora da entrevista.');
      return;
    }
    const at = `${date}T${time}`;
    const notifyAt = notifyAtFor(at, option, custom);
    const problem = validateInterview(at, notifyAt);
    if (problem || !notifyAt) {
      setError(problem ?? 'Escolha quando quer ser avisado.');
      return;
    }
    setSaving(true);
    try {
      onSaved(await salesApi.createInterview(proposal.id, { who, at, notifyAt }), notifyAt);
    } catch (saveError) {
      setError(errorMessage(saveError, 'Não consegui salvar o lembrete.'));
      setSaving(false);
    }
  };

  return (
    <div
      className="scrim center"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form className="pm" noValidate onSubmit={submit}>
        <h3>Entrevista médica</h3>
        <div className="muted">
          {proposal.requestNumber ? `${proposal.requestNumber} ` : ''}
          {proposal.company}
        </div>
        <label className="field">
          <span>Quem vai fazer</span>
          <select value={who} onChange={(event) => setWho(event.target.value)}>
            {(options.length ? options : ['TITULAR']).map((name) => (
              <option key={name} value={name}>
                {name === ALL_BENEFICIARIES ? `${name} (${names.length})` : name}
              </option>
            ))}
          </select>
        </label>
        <div className="fgrid">
          <label className="field">
            <span>Data</span>
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          <label className="field">
            <span>Hora</span>
            <input
              ref={timeInput}
              type="time"
              step={300}
              value={time}
              onChange={(event) => setTime(event.target.value)}
            />
          </label>
        </div>
        <label className="field">
          <span>Me avisar para lembrar o cliente</span>
          <select value={option} onChange={(event) => setOption(event.target.value)}>
            {NOTIFY_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        {option === 'x' && (
          <label className="field">
            <span>Avisar em</span>
            <input
              type="datetime-local"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
            />
          </label>
        )}
        {error && <div className="err">{error}</div>}
        <div className="factions">
          <span style={{ flex: 1 }} />
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn" type="submit" disabled={saving}>
            Salvar lembrete
          </button>
        </div>
      </form>
    </div>
  );
}

/** As linhas de entrevista dentro do card. */
export function InterviewLines({
  proposal,
  onUpdate,
  onDelete,
}: {
  proposal: SalesProposal;
  onUpdate: (interview: SalesInterview, patch: { done?: boolean }) => void;
  onDelete: (interview: SalesInterview) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(null), 3000);
    return () => window.clearTimeout(timer);
  }, [armed]);
  if (!proposal.interviews.length) return null;
  return (
    <>
      {[...proposal.interviews]
        .sort((a, b) => a.at.localeCompare(b.at))
        .map((interview) => (
          <div key={interview.id} className={`entline${interview.done ? ' ok' : ''}`}>
            {interview.done ? '✓ ' : ''}Entrevista {formatLocalShort(interview.at)} ·{' '}
            {interview.who}
            {!interview.done && (
              <span style={{ opacity: 0.8 }}>
                {' '}
                aviso {formatLocalShort(interview.notifyAt)}
              </span>
            )}
            <span className="x">
              {interview.done ? (
                <button type="button" onClick={() => onUpdate(interview, { done: false })}>
                  Desfazer
                </button>
              ) : (
                <>
                  <a
                    href={calendarLink(proposal, interview)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Agenda
                  </a>
                  <button type="button" onClick={() => onUpdate(interview, { done: true })}>
                    Feita
                  </button>
                </>
              )}
              <button
                type="button"
                title="Apagar"
                onClick={() => {
                  if (armed === interview.id) onDelete(interview);
                  else setArmed(interview.id);
                }}
              >
                {armed === interview.id ? 'Apagar?' : '×'}
              </button>
            </span>
          </div>
        ))}
    </>
  );
}

/** Painel do topo: próximos 7 dias e as que já passaram sem marcar como feita. */
export function InterviewsPanel({
  proposals,
  onDone,
}: {
  proposals: SalesProposal[];
  onDone: (interview: SalesInterview) => void;
}) {
  const clock = useClock(60_000);
  const now = toFortalezaLocal(new Date(clock));
  const limit = toFortalezaLocal(new Date(clock + 7 * 86_400_000));
  const rows = proposals
    .flatMap((proposal) => proposal.interviews.map((interview) => ({ proposal, interview })))
    .filter(({ interview }) => !interview.done && interview.at <= limit)
    .sort((a, b) => a.interview.at.localeCompare(b.interview.at));
  if (!rows.length) return null;
  return (
    <section className="ents">
      <div className="alhead">
        <h2>Entrevistas médicas</h2>
        <span>Próximos 7 dias e as que já passaram sem marcar como feita.</span>
      </div>
      {rows.map(({ proposal, interview }) => {
        const late = interview.at < now;
        const label = when(interview.at, clock);
        return (
          <div key={interview.id} className={`entrow${late ? ' late' : ''}`}>
            <div className="when num">
              {label.top}
              <small>{late ? 'passou' : label.sub}</small>
            </div>
            <div className="what">
              <b>{interview.who}</b>
              <span className="muted" style={{ fontSize: '.85rem' }}>
                {proposal.requestNumber ?? ''} · {proposal.company} · aviso{' '}
                {formatLocalShort(interview.notifyAt)}
              </span>
            </div>
            <div className="btns">
              <a
                className="act"
                href={calendarLink(proposal, interview)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Agenda
              </a>
              <button
                type="button"
                className="act go"
                style={{
                  ['--c' as string]: 'var(--enc)',
                  ['--cs' as string]: 'var(--enc-soft)',
                }}
                onClick={() => onDone(interview)}
              >
                Feita
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

let audio: AudioContext | null = null;
/** O navegador só deixa tocar som depois de um toque na página. */
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    () => {
      try {
        audio ??= new AudioContext();
      } catch {
        // Sem áudio: o aviso continua na tela e no título da aba.
      }
    },
    { once: true },
  );
}

/** 3 toques de 880 Hz, como no artefato. */
function beep() {
  try {
    if (!audio) return;
    for (const start of [0, 0.35, 0.7]) {
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.15;
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start(audio.currentTime + start);
      oscillator.stop(audio.currentTime + start + 0.2);
    }
  } catch {
    // Sem som; segue o aviso visual.
  }
}

/**
 * O aviso fixo "Lembrar o cliente da entrevista" quando chega a hora, com bipe e o título
 * da aba "(N) Lembrar entrevista". Confere a cada 20 s. "Já lembrei" e "Lembrar em 10 min"
 * ficam gravados no servidor — valem no celular e no computador.
 */
export function InterviewReminder({
  proposals,
  onAction,
}: {
  proposals: SalesProposal[];
  onAction: (
    interview: SalesInterview,
    patch: { reminded?: boolean; snoozeMinutes?: number },
  ) => void;
}) {
  const now = toFortalezaLocal(new Date(useClock(20_000)));
  const due = proposals
    .flatMap((proposal) => proposal.interviews.map((interview) => ({ proposal, interview })))
    .filter(
      ({ interview }) => !interview.done && !interview.remindedAt && interview.notifyAt <= now,
    )
    .sort((a, b) => a.interview.notifyAt.localeCompare(b.interview.notifyAt));
  const first = due[0];
  const shown = useRef<string | null>(null);
  const baseTitle = useRef(document.title);

  useEffect(() => {
    if (!first) {
      document.title = baseTitle.current;
      shown.current = null;
      return;
    }
    document.title = `(${due.length}) Lembrar entrevista`;
    if (shown.current !== first.interview.id) {
      shown.current = first.interview.id;
      beep();
    }
  }, [first, due.length]);
  useEffect(() => {
    const title = baseTitle.current;
    return () => {
      document.title = title;
    };
  }, []);

  if (!first) return null;
  return (
    <div className="lembrete" role="alert">
      <h3>Lembrar o cliente da entrevista</h3>
      <div>
        <b>{first.interview.who}</b>
        <br />
        <span className="muted">
          {first.proposal.requestNumber ?? ''} · {first.proposal.company}
        </span>
        <br />
        Entrevista com a enfermeira <b>{formatLocalShort(first.interview.at)}</b>
      </div>
      <div className="lb">
        <button
          type="button"
          className="btn"
          onClick={() => onAction(first.interview, { reminded: true })}
        >
          Já lembrei o cliente
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={() => onAction(first.interview, { snoozeMinutes: 10 })}
        >
          Lembrar em 10 min
        </button>
      </div>
      {due.length > 1 && (
        <div className="muted" style={{ fontSize: '.85rem' }}>
          + {due.length - 1} {due.length === 2 ? 'outro lembrete' : 'outros lembretes'}
        </div>
      )}
    </div>
  );
}
